import { useState, useMemo, useContext } from 'react';
import { Box, Typography } from '@mui/material';
import AIAssistantIcon from '@/components/shared/icons/AIAssistantIcon';
import { useAlineacionesContext } from '../hooks/useAlineacionesContext';
import AIWriterMessage from '@/DesignSystem/features/AIWriterMessage';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import { getMasterPhotoById } from '@/helpers/images-helper';
import CustomModal from '@/DesignSystem/components/custom-modal/CustomModal';
import { Context } from '@/context';

const LineupJarvisModal = ({ open, onClose, teamId }) => {
    const { editorLineups } = useAlineacionesContext();
    const { state } = useContext(Context);
    const nickname = state?.user?.nickname ? `, ${state?.user?.nickname}` : '';

    // Función para obtener el color basado en el porcentaje
    const getColorByPercentage = (count, total) => {
        const percentage = (count / total) * 100;

        if (percentage >= 90) {
            return '#1a5a8d'; // Azul
        } else if (percentage >= 70) {
            return '#206945'; // Verde
        } else if (percentage >= 50) {
            return '#A55113'; // Naranja
        } else {
            return '#963c3c'; // Rojo
        }
    };

    // Filtrar las alineaciones del equipo actual
    const teamEditorLineups = useMemo(() => {
        if (!teamId) return [];
        return editorLineups?.lineups?.filter((lineup) => lineup.team === teamId) || [];
    }, [editorLineups?.lineups, teamId]);

    // Analizar coincidencias de jugadores titulares (grid > 10 y grid < 60)
    const analisisCoincidencias = useMemo(() => {
        if (teamEditorLineups.length === 0) return null;

        const totalEditores = teamEditorLineups.length;
        const jugadoresConteo = new Map(); // Map<playerId, { count, player, editors }>

        // Contar cuántos editores tienen cada jugador como titular
        teamEditorLineups.forEach((lineup) => {
            const titulares = lineup.players?.filter((p) => p.grid > 10 && p.grid < 60) || [];
            titulares.forEach((jugador) => {
                if (!jugadoresConteo.has(jugador.id)) {
                    jugadoresConteo.set(jugador.id, {
                        count: 0,
                        player: jugador,
                        editors: [],
                    });
                }
                const data = jugadoresConteo.get(jugador.id);
                data.count += 1;
                data.editors.push(lineup.editor?.nickname || 'Editor');
            });
        });

        // Agrupar por cantidad de coincidencias
        const todosCoinciden = []; // 100% de editores
        const casiTodos = []; // 80% o más pero menos de 100%
        const mayoria = []; // 60% o más pero menos de 80%
        const algunos = []; // Menos de 60%

        jugadoresConteo.forEach((data) => {
            const porcentaje = (data.count / totalEditores) * 100;

            if (data.count === totalEditores) {
                todosCoinciden.push(data);
            } else if (porcentaje >= 80) {
                casiTodos.push(data);
            } else if (porcentaje >= 60) {
                mayoria.push(data);
            } else {
                algunos.push(data);
            }
        });

        // Ordenar cada grupo por posición (grid)
        const sortByGrid = (a, b) => a.player.grid - b.player.grid;
        todosCoinciden.sort(sortByGrid);
        casiTodos.sort(sortByGrid);
        mayoria.sort(sortByGrid);
        algunos.sort(sortByGrid);

        return {
            totalEditores,
            todosCoinciden,
            casiTodos,
            mayoria,
            algunos,
        };
    }, [teamEditorLineups]);

    if (!analisisCoincidencias) return null;

    const { totalEditores, todosCoinciden, casiTodos, mayoria, algunos } = analisisCoincidencias;

    // Combinar todos los jugadores que no son 100% en "Otros jugadores"
    const otrosJugadores = [...casiTodos, ...mayoria, ...algunos];

    return (
        <CustomModal open={open} onClose={onClose} title={'Análisis de Jarvis'} maxWidth="md">
            {/* Mensaje de bienvenida */}
            <Box mb={3} display="flex" alignItems="flex-start" gap={0.5}>
                <AIAssistantIcon width={60} height={60} isMarvin={false} />
                <Box flex={1}>
                    <AIWriterMessage
                        messages={[
                            `Hola${nickname}, soy Jarvis y te resumiré la opinión de nuestros expertos sobre las alineaciones probables.`,
                        ]}
                        speed={30}
                        fontSize={14}
                        color="text.secondary"
                    />
                </Box>
            </Box>

            {/* Titulares según todos */}
            {todosCoinciden.length > 0 && (
                <Box mb={4}>
                    <Typography variant="h6" color="success.main" mb={0} fontWeight={400}>
                        Titulares según todos ({totalEditores}/{totalEditores} expertos)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontSize={14} mb={1}>
                        Estos jugadores son considerados titulares por todos nuestros expertos.
                    </Typography>
                    <Box display="flex" flexWrap="wrap">
                        {todosCoinciden.map((data) => (
                            <Box
                                key={data.player.id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: { xs: '25%', sm: '20%' },
                                }}
                            >
                                <Box position="relative">
                                    <CustomImageComponent
                                        src={getMasterPhotoById(data.player.id, 90)}
                                        alt={data.player.name}
                                        width={70}
                                        height={70}
                                    />
                                </Box>
                                <Typography
                                    variant="caption"
                                    textAlign="center"
                                    mt={0.5}
                                    fontSize={12}
                                    noWrap
                                    maxWidth="70px"
                                >
                                    {data.player.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Otros jugadores */}
            {otrosJugadores.length > 0 && (
                <Box mb={1}>
                    <Typography variant="h6" color="warning.main" fontWeight={400}>
                        Otros jugadores
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1} fontSize={14}>
                        Jugadores con diferentes niveles de consenso entre los expertos.
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                        {otrosJugadores.map((data) => (
                            <Box
                                key={data.player.id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    width: '70px',
                                }}
                            >
                                <Box position="relative">
                                    <CustomImageComponent
                                        src={getMasterPhotoById(data.player.id, 90)}
                                        alt={data.player.name}
                                        width={70}
                                        height={70}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            right: 0,
                                            bgcolor: getColorByPercentage(data.count, totalEditores),
                                            color: '#fff',
                                            borderRadius: '4px',
                                            px: 0.5,
                                            py: 0.25,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            width: '20px',
                                            height: '20px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        {data.count}
                                    </Box>
                                </Box>
                                <Typography
                                    variant="caption"
                                    textAlign="center"
                                    mt={0.5}
                                    fontSize={12}
                                    noWrap
                                    maxWidth="70px"
                                >
                                    {data.player.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </CustomModal>
    );
};

const LineupJarvisComponent = ({ teamId }) => {
    const [openModal, setOpenModal] = useState(false);
    const { editorLineups } = useAlineacionesContext();

    // Contar editores con alineaciones para este equipo
    const editorsCount = useMemo(() => {
        if (!teamId) return 0;
        return editorLineups?.lineups?.filter((lineup) => lineup.team === teamId)?.length || 0;
    }, [editorLineups?.lineups, teamId]);

    // Solo mostrar si hay al menos 3 editores
    if (editorsCount < 3) return null;

    return (
        <>
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '55px',
                    right: '25px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                        transform: 'scale(1.1)',
                    },
                }}
                onClick={() => setOpenModal(true)}
            >
                <AIAssistantIcon width={70} height={70} isMarvin={false} />
            </Box>

            <LineupJarvisModal open={openModal} onClose={() => setOpenModal(false)} teamId={teamId} />
        </>
    );
};

export default LineupJarvisComponent;
